import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getApiBase } from '../config/apiBase';
import { getToken, setToken as storeToken, clearSession, getLastActivity, setLastActivity } from '../utils/session';

const AuthContext = createContext();
const BASE = getApiBase();

/** Shared with the server, which sends its own value back on login. */
const DEFAULT_IDLE_MINUTES = 30;
/** How long the warning sits on screen before the session actually ends. */
const WARN_SECONDS = 60;


export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => getToken());
  const [loading, setLoading] = useState(true);
  const [idleMinutes, setIdleMinutes] = useState(DEFAULT_IDLE_MINUTES);
  // Seconds left once the warning is up; null while the session is healthy.
  const [idleCountdown, setIdleCountdown] = useState(null);

  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);
  const lastBeatRef = useRef(0);

  /**
   * Ask the server for a fresh token, at most once a minute.
   *
   * `force` skips that throttle, which the "Stay signed in" button needs: by the
   * time the warning shows, the current token has under a minute left, so resetting
   * the idle clock without renewing would sign the person out anyway — seconds
   * after they said they were still there.
   */
  const renewToken = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && now - lastBeatRef.current < 60000) return;
    if (!tokenRef.current) return;
    lastBeatRef.current = now;
    try {
      const res = await fetch(`${BASE}/auth/heartbeat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokenRef.current}` },
      });
      const json = await res.json();
      if (json && json.success && json.data && json.data.token) {
        storeToken(json.data.token);
        setToken(json.data.token);
        if (json.data.idle_timeout_minutes) setIdleMinutes(json.data.idle_timeout_minutes);
      }
    } catch { /* a blip here costs nothing; the next interaction renews */ }
  }, []);

  // ── On mount: validate stored token ──
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    (async () => {
      try {
        const res = await fetch(`${BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (json.success) {
          setUser(json.data.user);
          setToken(json.data.token);
          storeToken(json.data.token);
        } else {
          clearSession();
          setToken(null);
          setUser(null);
        }
      } catch {
        clearSession();
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-next-line

  const login = async (email, password) => {
    const res = await fetch(`${BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.message);
    storeToken(json.data.token);
    if (json.data.idle_timeout_minutes) setIdleMinutes(json.data.idle_timeout_minutes);
    setLastActivity(Date.now());
    setToken(json.data.token);
    setUser(json.data.user);
    return json.data;
  };

  /**
   * Sign out, telling the server first so the departure is recorded.
   *
   * audit_logs held 644 logins and no logouts, which is why nobody could say how
   * long anyone had spent in the system. The request is best-effort: if it fails,
   * the local session still ends.
   */
  const logout = useCallback(async (reason) => {
    const current = tokenRef.current;
    if (current) {
      try {
        await fetch(`${BASE}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${current}` },
          body: JSON.stringify({ reason: reason || 'manual' }),
          keepalive: true,
        });
      } catch { /* leaving anyway */ }
    }
    clearSession();
    setIdleCountdown(null);
    setToken(null);
    setUser(null);
  }, []);

  /**
   * Idle timeout.
   *
   * The token now lives only as long as the idle limit, and this is what renews it
   * while someone is working. Only real interaction counts — background polling is
   * deliberately excluded, because the SAP screens refresh themselves every ten
   * seconds and would otherwise hold a session open in an empty room all night.
   */
  useEffect(() => {
    if (!token || !user) return undefined;

    const idleMs = idleMinutes * 60 * 1000;
    const warnAtMs = Math.max(idleMs - WARN_SECONDS * 1000, idleMs / 2);

    const markActive = () => {
      setLastActivity(Date.now());
      setIdleCountdown(null);
      renewToken();
    };

    const events = ['mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];
    for (const e of events) window.addEventListener(e, markActive, { passive: true });
    if (!getLastActivity()) setLastActivity(Date.now());

    const tick = setInterval(() => {
      const last = getLastActivity() || Date.now();
      const idleFor = Date.now() - last;
      if (idleFor >= idleMs) {
        logout('idle');
      } else if (idleFor >= warnAtMs) {
        setIdleCountdown(Math.ceil((idleMs - idleFor) / 1000));
      }
    }, 1000);

    return () => {
      clearInterval(tick);
      for (const e of events) window.removeEventListener(e, markActive);
    };
  }, [token, user, idleMinutes, logout, renewToken]);

  // The API layer clears the token when the server refuses it; this is how the rest
  // of the app finds out, so a tab left open overnight lands on the login screen
  // instead of a page of error messages.
  useEffect(() => {
    const onExpired = () => {
      setIdleCountdown(null);
      setToken(null);
      setUser(null);
    };
    window.addEventListener('mrp:session-expired', onExpired);
    return () => window.removeEventListener('mrp:session-expired', onExpired);
  }, []);

  // ── Permission helpers ──
  const hasModuleAccess = useCallback((moduleKey) => {
    if (!user) return false;
    if (['Super Admin', 'Admin'].includes(user.role_name)) return true;
    return !!user.permissions?.[moduleKey];
  }, [user]);

  const canCreate = user?.can_create ?? false;
  const canEdit   = user?.can_edit ?? false;
  const canDelete = user?.can_delete ?? false;

  const staySignedIn = useCallback(() => {
    setLastActivity(Date.now());
    setIdleCountdown(null);
    renewToken(true);
  }, [renewToken]);

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, logout,
      hasModuleAccess, canCreate, canEdit, canDelete,
      isAuthenticated: !!user && !!token,
      idleMinutes, idleCountdown, staySignedIn,
    }}>
      {children}
      {idleCountdown != null && user && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-lg shadow-2xl p-6 max-w-sm mx-4 text-center">
            <h2 className="text-base font-semibold text-gray-900">Still there?</h2>
            <p className="text-sm text-gray-600 mt-2">
              You have not used the system for {idleMinutes} minutes. For security you will be
              signed out in <strong>{idleCountdown}</strong> second{idleCountdown === 1 ? '' : 's'}.
            </p>
            <div className="mt-4 flex gap-2 justify-center">
              <button
                type="button"
                onClick={staySignedIn}
                className="px-4 py-2 rounded bg-sap-blue text-white text-sm font-semibold hover:opacity-90"
              >
                Stay signed in
              </button>
              <button
                type="button"
                onClick={() => logout('manual')}
                className="px-4 py-2 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
              >
                Sign out now
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
